import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, tap, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStore } from '../store/auth.store';
import { StorageService } from './storage.service';
import { TokenRefreshService } from './token-refresh.service';
import { SessionService } from './session.service';
import { IdleService } from './idle.service';
import {
  LoginRequest,
  LoginResponse,
  AuthSession,
  ChangePasswordRequest,
  ChangePasswordResponse,
  UserProfile,
  PermissionItem,
} from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http           = inject(HttpClient);
  private router         = inject(Router);
  private authStore      = inject(AuthStore);
  private storageService = inject(StorageService);
  private tokens         = inject(TokenRefreshService);
  private session        = inject(SessionService);
  private idle           = inject(IdleService);

  readonly currentUser            = this.authStore.user;
  readonly currentCompany         = this.authStore.company;
  readonly currentUserProfile     = this.authStore.userProfile;
  readonly isAuthenticated        = this.authStore.isAuthenticated;
  readonly idToken                = this.authStore.idToken;
  readonly requiresPasswordChange = this.authStore.requiresPasswordChange;

  /**
   * Llamado por APP_INITIALIZER. Restaura la sesión del storage y espera
   * a que /users/me responda antes de que el router active cualquier guard.
   */
  init(): Observable<void> {
    return this.restoreSession();
  }

  /** Código de error interno para usuarios sin rol asignado. */
  static readonly NO_ROLE_ERROR = 'NO_ROLE_ASSIGNED';

  login(credentials: LoginRequest, rememberMe: boolean): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/login`, credentials)
      .pipe(
        tap((response) => {
          const expiresAt = Date.now() + parseInt(response.expiresIn, 10) * 1000;
          const session: AuthSession = {
            idToken: response.idToken,
            refreshToken: response.refreshToken,
            expiresAt,
            user: response.user,
            company: response.company,
            rememberMe,
            requiresPasswordChange: response.requiresPasswordChange,
          };
          this.storageService.saveSession(session);
          this.authStore.setSession(session);
          this.idle.reset();
        }),
        switchMap((response) =>
          this.getMe().pipe(
            tap((profile) => this.authStore.setUserProfile(profile)),
            switchMap((profile) => {
              if (this.profileHasNoRole(profile)) {
                this.discardSession();
                return throwError(() => ({ code: AuthService.NO_ROLE_ERROR }));
              }
              return [response];
            }),
          ),
        ),
      );
  }

  silentReLogin(newPassword: string): Observable<LoginResponse> {
    const email      = this.authStore.userEmail();
    const rememberMe = this.storageService.getSession()?.rememberMe ?? false;
    return this.login({ email, password: newPassword }, rememberMe);
  }

  changePasswordFromProfile(currentPassword: string, newPassword: string, confirmNewPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiUrl}/configuration/change-password`,
      {
        current_password:     currentPassword,
        new_password:         newPassword,
        confirm_new_password: confirmNewPassword,
      },
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<ChangePasswordResponse> {
    const email = this.authStore.userEmail();
    const body: ChangePasswordRequest = {
      email,
      current_password: currentPassword,
      new_password: newPassword,
    };
    return this.http.post<ChangePasswordResponse>(
      `${environment.apiUrl}/auth/change-password`,
      body,
    );
  }

  clearPasswordChangeFlag(): void {
    const session = this.storageService.getSession();
    if (session) {
      this.storageService.saveSession({ ...session, requiresPasswordChange: false });
    }
    this.authStore.clearPasswordChangeFlag();
  }

  logout(): void {
    this.session.logout();
  }

  getMe(): Observable<UserProfile> {
    // TODO(pruebas): eliminar el map cuando el backend asigne los permisos de tenant al usuario
    // const TENANT_PERMISSIONS_TEST: PermissionItem[] = [
    //   { code: 'tenants:view',         name: 'Ver empresas',                    description: 'Listado y detalle de empresas (superadmin)' },
    //   { code: 'tenants:create',       name: 'Crear empresas',                  description: 'Alta de nuevas empresas/tenants (superadmin)' },
    //   { code: 'tenants:edit',         name: 'Editar empresas',                 description: 'Edición de datos de empresa (superadmin)' },
    //   { code: 'tenants:activate',     name: 'Activar/desactivar empresas',     description: 'Cambia el estado de una empresa (superadmin)' },
    //   { code: 'tenants:manage_users', name: 'Gestionar usuarios de empresas',  description: 'CRUD de usuarios de cualquier tenant (superadmin)' },
    // ];

    return this.http.get<UserProfile>(`${environment.apiUrl}/api/v1/users/me`).pipe(
      map((profile) => {
        const existingCodes = new Set(profile.permissions.map(p => p.code));
        // const toAdd = TENANT_PERMISSIONS_TEST.filter(p => !existingCodes.has(p.code));
        // return { ...profile, permissions: [...profile.permissions, ...toAdd] };
        return profile;
      }),
    );
  }

  /**
   * Un usuario sin rol no es superadmin (no tiene tenants:view) y no tiene role_name.
   * El superadmin puede no tener role_name asignado y aun así debe poder acceder.
   */
  private profileHasNoRole(profile: UserProfile): boolean {
    const isSuperAdmin = profile.permissions.some(p => p.code === 'tenants:view');
    return !isSuperAdmin && !profile.role_name;
  }

  private restoreSession(): Observable<void> {
    const session = this.storageService.getSession();

    if (!session) {
      return of(void 0);
    }

    this.authStore.setSession(session);
    this.idle.reset();

    // Una sesión vencida sigue siendo recuperable con el refresh token: solo se
    // descarta si la renovación falla.
    const validToken$ = this.storageService.isSessionExpired(session)
      ? this.tokens.refresh()
      : of(session.idToken);

    return validToken$.pipe(
      switchMap(() => this.getMe()),
      tap((profile) => this.authStore.setUserProfile(profile)),
      switchMap((profile) => {
        if (this.profileHasNoRole(profile)) {
          this.discardSession();
          this.router.navigate(['/auth/sing-in'], { queryParams: { reason: 'no_role' } });
        }
        return of(void 0);
      }),
      catchError(() => {
        this.discardSession();
        this.router.navigate(['/auth/sing-in']);
        return of(void 0);
      }),
    );
  }

  private discardSession(): void {
    this.authStore.clearSession();
    this.storageService.clearSession();
  }
}
